import { Column } from "primereact/column";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { configProps, LocationEntry } from "../../types";
import React, { useState } from "react";
import { useAppDispatch } from "../../redux/store";
import { Button } from "primereact/button";
import { addNewSlice } from "../../redux/slices/addNew";
import { LocationCreate } from "../../api";
import DeleteLocation from "./DeleteLocation";
import { HistoryDialog } from "../../components/HistoryDialog";
import { CommentsDialog } from "../../components/CommentsDialog";

type LocationsTableProps = {
  data: LocationEntry[];
  columns: configProps<"locations">["columns"];
  isLoading: boolean;
  page: number;
  rowsPerPage: number;
  onPage: (e: DataTablePageEvent) => void;
  totalRecords?: number;
  sortField: string;
  sortOrder: number;
  onSort: (e: DataTableSortEvent) => void;
};

export default function LocationsTable({
  data,
  columns,
  isLoading,
  page,
  rowsPerPage,
  onPage,
  totalRecords,
  sortField,
  sortOrder,
  onSort,
}: LocationsTableProps) {
  const dispatch = useAppDispatch();

  const [selectedHistoryRow, setSelectedHistoryRow] =
    useState<LocationEntry | null>(null);
  const [selectedCommentsRow, setSelectedCommentsRow] =
    useState<LocationEntry | null>(null);

  const newClickBody = React.useCallback(
    (rowData: LocationEntry) => {
      return (
        <div className="flex-container">
          <Button
            size="small"
            icon="pi pi-pencil"
            text
            onClick={() => {
              dispatch(
                addNewSlice.actions.openAddNewDialog({
                  target: {
                    field: "address",
                    type: "address",
                    initialValues: {
                      id: rowData.id,
                      address: rowData.address,
                      city: rowData.city,
                      country: rowData.country,
                      latitude: rowData.latitude,
                      longitude: rowData.longitude,
                      contact_person_name: rowData.contactPersonName,
                      contact_phone_number: rowData.contactPersonPhone,
                    } as Partial<LocationCreate> & { id: number },
                  },
                }),
              );
            }}
          />
          <Button
            size="small"
            icon="pi pi-history"
            text
            onClick={(e) => {
              e.stopPropagation();
              setSelectedHistoryRow(rowData);
            }}
          />
          <Button
            size="small"
            className="wide"
            icon="pi pi-comment"
            text={rowData.commentsCount === 0}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCommentsRow(rowData);
            }}
          />
          <DeleteLocation rowData={rowData} />
        </div>
      );
    },

    [dispatch],
  );
  return (
    <>
      <DataTable
        value={data}
        dataKey="id"
        size="small"
        lazy
        paginator
        rows={rowsPerPage}
        first={page * rowsPerPage}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sortField={sortField}
        sortOrder={(sortOrder ?? 1) as 1 | -1}
        onSort={onSort}
        scrollable
        scrollHeight="75vh"
        emptyMessage={
          isLoading ? "Lokalizacje ładowania..." : "Nie znaleziono danych!"
        }
      >
        {columns
          .filter((entry) => entry !== undefined)
          .map((entry) => {
            return (
              <Column
                key={entry.field}
                field={entry.field}
                header={entry.header}
                sortable={entry.sortable ?? false}
                body={entry.body}
              />
            );
          })}
        <Column className="icon-column" body={newClickBody} />
      </DataTable>
      <HistoryDialog<"locations">
        rowData={selectedHistoryRow}
        onHide={() => setSelectedHistoryRow(null)}
        columns={columns}
      />
      <CommentsDialog<"locations">
        rowData={selectedCommentsRow}
        onHide={() => setSelectedCommentsRow(null)}
        columns={columns}
      />
    </>
  );
}
